import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule, NgClass, NgStyle } from '@angular/common';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-wheel-slider',
  template: `
    <div #sliderRef [ngClass]="'wheel keen-slider wheel--perspective-' + perspective">
      <div
        class="wheel__shadow-top"
        [ngStyle]="{
          transform: 'translateZ(' + radius + 'px)',
          '-webkit-transform': 'translateZ(' + radius + 'px)',
        }"
      ></div>
      <div class="wheel__inner">
        <div class="wheel__slides" [ngStyle]="{ width: width + 'px' }">
          @for (slide of slideValues; track slide.value) {
            <div class="wheel__slide" [ngStyle]="slide.style">
              <span>{{ slide.value }}</span>
            </div>
          }
        </div>
      </div>
      <div
        class="wheel__shadow-bottom"
        [ngStyle]="{
          transform: 'translateZ(' + radius + 'px)',
          '-webkit-transform': 'translateZ(' + radius + 'px)',
        }"
      ></div>
    </div>
  `,
  imports: [NgClass, NgStyle],
  styles: [
    `
      .wheel {
        color: var(--deep-slate-gray);
        display: block;
        height: 100%;
        overflow: visible;
        width: 100%;
      }

      .wheel--perspective-right .wheel__inner {
        perspective-origin: calc(50% + 100px) 50%;
        transform: translateX(10px);
        -webkit-transform: translateX(10px);
      }

      .wheel--perspective-left .wheel__inner {
        perspective-origin: calc(50% - 100px) 50%;
        transform: translateX(-10px);
        -webkit-transform: translateX(-10px);
      }

      .wheel__inner {
        display: flex;
        align-items: center;
        justify-content: center;
        perspective: 1000px;
        transform-style: preserve-3d;
        height: 16%;
        width: 100%;
        user-select: none;
        -moz-user-select: none;
      }

      .wheel__slides {
        height: 100%;
        position: relative;
        width: 100%;
      }

      .wheel__shadow-top,
      .wheel__shadow-bottom {
        background: linear-gradient(to bottom, rgba(var(--white-rgb), 0.9) 0%, rgba(var(--white-rgb), 0.5) 100%);
        left: 0;
        height: calc(42% + 2px);
        width: 100%;
        border-bottom: 1px solid rgba(var(--black-rgb), 0.3);
        position: relative;
        margin-top: -2px;
        z-index: 5;
      }

      .wheel__shadow-bottom {
        background: linear-gradient(to bottom, rgba(var(--white-rgb), 0.5) 0%, rgba(var(--white-rgb), 0.9) 100%);
        margin-top: 2px;
        border-bottom: none;
        border-top: 0.5px solid rgba(var(--black-rgb), 0.3);
      }

      .wheel__label {
        font-weight: 500;
        font-size: 15px;
        line-height: 1;
        margin-top: 1px;
        margin-left: 5px;
      }

      .wheel__slide {
        align-items: center;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        display: flex;
        font-size: 20px;
        font-weight: 400;
        height: 100%;
        width: 100%;
        position: absolute;
        justify-content: flex-end;
      }
    `,
  ],
})
export class WheelComponent implements AfterViewInit, OnDestroy {
  @Input() loop: boolean;
  @Input() initIdx = 0;
  @Input() intervalDifference = 1;
  @Input() length: number;
  @Input() width: number;
  @Input() perspective = 'center';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  @Input() setValue: Function | undefined;
  @ViewChild('sliderRef') sliderRef: ElementRef<HTMLElement>;
  @Input() type: 'hours' | 'minutes';
  @Input() previousSelectedTime: string;

  @Output() valueSelected = new EventEmitter<string>();

  height: number;
  slidesPerView = 1;
  slideDegree: number = 360 / 20;
  slider: KeenSliderInstance = null;
  slideValues: any;
  radius = 0;
  wheelSize = 20;

  setSlideValues(details: { slides: { distance: number }[]; abs: number }) {
    const offset = this.loop ? 1 / 2 - 1 / this.slidesPerView / 2 : 0;

    const values = [];
    for (let i = 0; i < this.length; i++) {
      const distance = (details.slides[i].distance - offset) * this.slidesPerView;
      const rotate = Math.abs(distance) > this.wheelSize / 2 ? 180 : distance * (360 / this.wheelSize) * -1;
      const style = {
        transform: `rotateX(${rotate}deg) translateZ(${this.radius}px)`,
        WebkitTransform: `rotateX(${rotate}deg) translateZ(${this.radius}px)`,
      };
      // Adjust the value calculation based on the interval difference
      const actualValue = i * this.intervalDifference; // Use the interval difference here

      // Format the value to have leading zeros (00, 01, 02, etc.)
      const formattedValue = actualValue.toString().padStart(2, '0');

      const value = this.setValue ? this.setValue(i, details.abs + Math.round(distance), formattedValue) : formattedValue;
      values.push({ style, value });
    }
    this.slideValues = values;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initIdx = this.calculateInitialIndexForPreviousTime();
      if (this.loop) this.slidesPerView = 9;
      const options = {
        slides: {
          number: this.length,
          origin: this.loop ? 'center' : 'auto',
          perView: this.slidesPerView,
        },
        vertical: true,
        initial: this.initIdx || 0,
        loop: this.loop,
        dragSpeed: (val: number) => {
          return val * (this.height / ((this.height / 2) * Math.tan(this.slideDegree * (Math.PI / 180))) / this.slidesPerView);
        },
        created: (s: { size: number; track: { details: any } }) => {
          this.height = s.size;
          this.radius = this.height / 2;

          this.setSlideValues(s.track.details);
        },
        updated: (s: { size: number }) => {
          this.height = s.size;
        },
        detailsChanged: (s: { track: { details: any } }) => {
          this.setSlideValues(s.track.details);
          this.valueSelected.emit(this.formatValueForEmission(s.track.details.rel));
        },
        rubberband: !this.loop,
        mode: 'free-snap',
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.slider = new KeenSlider(this.sliderRef.nativeElement, options);
    });
  }

  formatValueForEmission(index: number): string {
    if (this.type === 'minutes') {
      // Convert index to minutes based on the intervalDifference
      const minutes = index * this.intervalDifference;
      return minutes < 10 ? `0${minutes}` : minutes.toString();
    } else if (this.type === 'hours') {
      // Assuming hours are straightforward and just need potential padding
      return index < 10 ? `0${index}` : index.toString();
    }
    return index.toString();
  }

  calculateInitialIndexForPreviousTime() {
    if (!this.previousSelectedTime) return this.initIdx;

    const [prevHours, prevMinutes] = this.previousSelectedTime.split(':').map(Number);
    let initialIndex = 0;

    if (this.type === 'hours') {
      initialIndex = prevHours + 1; // Ensure the time is in the future
    } else if (this.type === 'minutes') {
      // This calculation will depend on how your slider is set up for minutes.
      // Assuming minutes are in intervals and the entire day is selectable.
      const totalMinutes = prevHours * 60 + prevMinutes + this.intervalDifference; // Move to the next selectable interval
      initialIndex = Math.floor(totalMinutes / this.intervalDifference) % (60 / this.intervalDifference);
    }

    return Math.max(initialIndex, this.initIdx); // Ensure we don't set an index less than the initial index (if any)
  }

  ngOnDestroy() {
    if (this.slider) this.slider.destroy();
  }
}

@Component({
  selector: 'app-wheel',
  imports: [CommonModule, WheelComponent, TranslateModule],
  templateUrl: './time-wheel.component.html',
  styleUrls: ['./time-wheel.component.scss'],
})
export class TimeWheelComponent implements OnInit {
  selectedHours = '0';
  selectedMinutes = '0';
  @Output() timeSelected = new EventEmitter<string>();
  previousSelectedTime: string;
  private readonly _bottomSheetRef = inject(NgxBottomSheetService);

  ngOnInit() {
    const currentTime = this._bottomSheetRef.currentSheetData.currentSelectedTime;
    this.previousSelectedTime = this._bottomSheetRef.currentSheetData.previousSelectedTime;

    if (currentTime) {
      let [hours, minutes] = currentTime.split(':');
      hours = String(parseInt(hours, 10));
      minutes = String(parseInt(minutes, 10));
      this.selectedHours = hours;
      this.selectedMinutes = minutes;
    }
  }

  onTimeSelected(value: string, type: 'hours' | 'minutes') {
    if (type === 'hours') {
      this.selectedHours = value;
    } else if (type === 'minutes') {
      this.selectedMinutes = value;
    }
  }

  selectTime() {
    const formattedTime = `${this.selectedHours.padStart(2, '0')}:${this.selectedMinutes.padStart(2, '0')}`;
    this.timeSelected.emit(formattedTime);
    this._bottomSheetRef.close(formattedTime);
  }

  cancel() {
    this._bottomSheetRef.close();
  }

  calculateInitialIndex(selectedMinutes: number): number {
    return Math.floor(selectedMinutes / 15);
  }
}
